import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Cloud, ArrowRight, Check, Minus, Layers } from "lucide-react";
import AppHeader from "../components/app/AppHeader";
import { api } from "../lib/api";
import { categoryToTier } from "../lib/archReducer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const PROVIDER_META = {
  azure: { label: "Microsoft Azure", color: "#0EA5E9", tag: "AZ" },
  aws: { label: "Amazon Web Services", color: "#F59E0B", tag: "AW" },
  gcp: { label: "Google Cloud", color: "#22C55E", tag: "GC" },
};

const TIER_META = {
  frontend: { color: "#5B5BD6", blurb: "Edge and delivery — what your users hit first." },
  compute: { color: "#0EA5E9", blurb: "Runs your application code." },
  data: { color: "#0D9488", blurb: "Stores or analyses your data." },
  networking: { color: "#F59E0B", blurb: "Connects and routes traffic between the other tiers." },
  security: { color: "#EF4444", blurb: "Guards identity, secrets, and traffic." },
};

const TABS = ["azure", "aws", "gcp"];

function Pill({ label, color, testId }) {
  return (
    <span
      className="font-mono"
      data-testid={testId}
      style={{
        padding: "2px 8px",
        borderRadius: 9999,
        fontSize: 10,
        letterSpacing: "0.08em",
        color,
        background: `${color}1F`,
        border: `1px solid ${color}66`,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

function ResourceCard({ provider, item, onOpen }) {
  const meta = PROVIDER_META[provider];
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`resource-card-${provider}-${item.name}`}
      className="rounded-[10px] border motion-canvas card-lift text-left w-full"
      style={{
        background: "var(--color-background-secondary)",
        borderColor: "var(--color-border-primary)",
        padding: 16,
        cursor: "pointer",
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <Pill label={meta.tag} color={meta.color} />
      </div>
      <h3
        className="text-text-primary"
        style={{ fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em", lineHeight: 1.3 }}
      >
        {item.name}
      </h3>
      <p
        className="text-text-tertiary mt-1.5"
        style={{ fontSize: 12, lineHeight: 1.5 }}
      >
        {item.description}
      </p>
    </button>
  );
}

function DetailRow({ icon, label, children }) {
  return (
    <div
      className="border-t pt-4 mt-4 first:border-0 first:pt-0 first:mt-0"
      style={{ borderColor: "var(--color-border-secondary)" }}
    >
      <div
        className="font-mono flex items-center gap-1.5 mb-2"
        style={{
          fontSize: 10,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--color-text-tertiary)",
          fontWeight: 600,
        }}
      >
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}

/**
 * Detail card for a single catalog service. Everything shown here is derived
 * from data we actually hold — the catalog description, the category, the tier
 * the category maps to on the canvas, and whether our Terraform generator has a
 * real mapping for it. Nothing is invented about the service.
 */
function ResourceDetailDialog({ provider, item, category, siblings, onOpenSibling, onClose }) {
  const navigate = useNavigate();
  if (!item) return null;

  const providerMeta = PROVIDER_META[provider];
  const tier = categoryToTier(category);
  const tierMeta = TIER_META[tier] || TIER_META.compute;
  const related = (siblings || []).filter((s) => s.name !== item.name).slice(0, 8);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="resource-detail-dialog"
        className="max-w-[560px] max-h-[85vh] overflow-y-auto"
        style={{
          background: "var(--color-background-secondary)",
          borderColor: "var(--color-border-primary)",
          borderRadius: 16,
        }}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Pill label={providerMeta.label} color={providerMeta.color} />
            <Pill label={category} color="#94A3B8" />
            <Pill label={`${tier} tier`} color={tierMeta.color} testId="resource-detail-tier" />
          </div>
          <DialogTitle
            className="text-text-primary"
            style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.015em", marginTop: 10 }}
            data-testid="resource-detail-name"
          >
            {item.name}
          </DialogTitle>
        </DialogHeader>

        <div>
          <DetailRow label="What it is">
            <p className="text-text-primary" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              {item.description}
            </p>
          </DetailRow>

          <DetailRow icon={<Layers size={11} />} label="Where it sits in an architecture">
            <p className="text-text-secondary" style={{ fontSize: 13, lineHeight: 1.6 }}>
              Added to a diagram, this lands in the{" "}
              <span style={{ color: tierMeta.color, fontWeight: 600 }}>{tier}</span> tier.{" "}
              {tierMeta.blurb}
            </p>
          </DetailRow>

          <DetailRow label="Infrastructure-as-code export">
            <p
              className="text-text-secondary inline-flex items-start gap-2"
              style={{ fontSize: 13, lineHeight: 1.6 }}
              data-testid="resource-detail-iac"
            >
              {item.iac_supported ? (
                <>
                  <Check
                    size={14}
                    color="var(--color-success)"
                    style={{ flexShrink: 0, marginTop: 3 }}
                  />
                  <span>
                    Exports to a real Terraform resource — it comes out of an IaC export ready to
                    review, not as a placeholder.
                  </span>
                </>
              ) : (
                <>
                  <Minus
                    size={14}
                    color="var(--color-text-tertiary)"
                    style={{ flexShrink: 0, marginTop: 3 }}
                  />
                  <span>
                    Not yet mapped in our Terraform generator. It still appears on the diagram and
                    in cost estimates; an IaC export emits a TODO stub for it.
                  </span>
                </>
              )}
            </p>
          </DetailRow>

          {related.length > 0 && (
            <DetailRow label={`Others in ${category}`}>
              <div className="flex flex-wrap gap-1.5">
                {related.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => onOpenSibling(s)}
                    data-testid={`resource-detail-related-${s.name}`}
                    className="motion-micro"
                    style={{
                      background: "var(--color-background-tertiary)",
                      border: "1px solid var(--color-border-primary)",
                      color: "var(--color-text-secondary)",
                      fontSize: 12,
                      padding: "5px 10px",
                      borderRadius: 9999,
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "var(--color-accent)";
                      e.currentTarget.style.color = "var(--color-text-primary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "var(--color-border-primary)";
                      e.currentTarget.style.color = "var(--color-text-secondary)";
                    }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </DetailRow>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            data-testid="resource-detail-close"
            className="motion-micro"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              height: 40,
              padding: "0 16px",
              borderRadius: 8,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => navigate("/projects/new")}
            data-testid="resource-detail-build"
            className="motion-micro inline-flex items-center gap-1.5"
            style={{
              background: "var(--color-accent)",
              color: "#0a0a0a",
              height: 40,
              padding: "0 16px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              border: "none",
              cursor: "pointer",
            }}
          >
            Build an architecture <ArrowRight size={14} />
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Resources() {
  const [catalog, setCatalog] = useState(null);
  const [active, setActive] = useState("azure");
  const [query, setQuery] = useState("");
  // { item, category } — the service whose info card is open.
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/cloud-resources")
      .then(({ data }) => {
        if (!cancelled) setCatalog(data.providers || []);
      })
      .catch(() => !cancelled && setCatalog([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const activeProvider = useMemo(
    () => (catalog || []).find((p) => p.id === active),
    [catalog, active],
  );

  const filtered = useMemo(() => {
    if (!activeProvider) return [];
    const q = query.trim().toLowerCase();
    if (!q) return activeProvider.categories;
    return activeProvider.categories
      .map((c) => ({
        ...c,
        resources: c.resources.filter(
          (r) =>
            r.name.toLowerCase().includes(q) ||
            r.description.toLowerCase().includes(q) ||
            c.name.toLowerCase().includes(q),
        ),
      }))
      .filter((c) => c.resources.length > 0);
  }, [activeProvider, query]);

  const totalForActive = activeProvider?.total ?? 0;
  const matchCount = filtered.reduce((acc, c) => acc + c.resources.length, 0);

  return (
    <div className="min-h-screen bg-bg-primary" data-testid="resources-page">
      <AppHeader />

      <main className="max-w-[1200px] mx-auto px-6 py-12">
        <header className="mb-8">
          <p
            className="font-mono text-text-tertiary mb-3"
            style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            Cloud Resource Library
          </p>
          <h1
            className="text-text-primary"
            style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.025em" }}
            data-testid="resources-headline"
          >
            Every service you can use
          </h1>
          <p className="text-text-secondary mt-2" style={{ fontSize: 14, lineHeight: 1.55 }}>
            The canonical catalog of {catalog ? catalog.reduce((a, p) => a + p.total, 0) : "—"} services across Azure, AWS, and GCP. Architectures we generate and templates we ship reference resources from this list.
          </p>
        </header>

        {/* Provider tabs + search */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="inline-flex p-1 rounded-[10px]" style={{
            background: "var(--color-background-secondary)",
            border: "1px solid var(--color-border-primary)",
          }} data-testid="resource-provider-tabs">
            {TABS.map((p) => {
              const meta = PROVIDER_META[p];
              const isActive = active === p;
              return (
                <button
                  key={p}
                  type="button"
                  data-testid={`resource-tab-${p}`}
                  onClick={() => setActive(p)}
                  className="motion-micro inline-flex items-center gap-2"
                  style={{
                    background: isActive ? "var(--color-background-tertiary)" : "transparent",
                    color: isActive ? "var(--color-text-primary)" : "var(--color-text-secondary)",
                    border: "none",
                    height: 36,
                    padding: "0 14px",
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 500,
                    cursor: "pointer",
                  }}
                >
                  <span
                    className="font-mono"
                    style={{
                      padding: "2px 7px",
                      borderRadius: 9999,
                      fontSize: 10,
                      letterSpacing: "0.06em",
                      color: meta.color,
                      background: `${meta.color}1F`,
                      border: `1px solid ${meta.color}66`,
                    }}
                  >
                    {meta.tag}
                  </span>
                  {meta.label}
                </button>
              );
            })}
          </div>

          <div className="relative" style={{ minWidth: 280 }}>
            <Search
              size={14}
              color="var(--color-text-tertiary)"
              style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${PROVIDER_META[active].label}…`}
              data-testid="resource-search-input"
              className="outline-none motion-micro"
              style={{
                width: "100%",
                height: 36,
                paddingLeft: 38,
                paddingRight: 14,
                borderRadius: 8,
                background: "var(--color-background-secondary)",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-primary)",
                fontSize: 13,
              }}
            />
          </div>
        </div>

        {/* Result count */}
        <p
          className="font-mono text-text-tertiary mb-6"
          style={{ fontSize: 11, letterSpacing: "0.04em" }}
        >
          {catalog === null
            ? "Loading…"
            : query
            ? `${matchCount} match${matchCount === 1 ? "" : "es"} in ${PROVIDER_META[active].label}`
            : `${totalForActive} services across ${activeProvider?.categories.length || 0} categories`}
        </p>

        {/* Categories */}
        {filtered.length === 0 ? (
          <div
            className="rounded-[12px] border text-center"
            style={{
              borderColor: "var(--color-border-primary)",
              background: "var(--color-background-secondary)",
              padding: 48,
            }}
            data-testid="resources-empty"
          >
            <Cloud size={28} color="var(--color-text-tertiary)" className="mx-auto mb-3" />
            <p className="text-text-primary" style={{ fontSize: 14, fontWeight: 500 }}>
              No matches
            </p>
            <p className="text-text-tertiary mt-1" style={{ fontSize: 12 }}>
              Try a different search term or switch providers.
            </p>
          </div>
        ) : (
          <div className="space-y-10" data-testid="resource-categories">
            {filtered.map((cat) => (
              <section key={cat.name} data-testid={`resource-category-${cat.name}`}>
                <div className="flex items-baseline gap-3 mb-4">
                  <h2
                    className="text-text-primary"
                    style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.005em" }}
                  >
                    {cat.name}
                  </h2>
                  <span
                    className="font-mono text-text-tertiary"
                    style={{ fontSize: 11, letterSpacing: "0.04em" }}
                  >
                    {cat.resources.length}
                  </span>
                </div>
                <div
                  className="grid gap-3"
                  style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}
                >
                  {cat.resources.map((r) => (
                    <ResourceCard
                      key={`${active}-${r.name}`}
                      provider={active}
                      item={r}
                      onOpen={(item) => setSelected({ item, category: cat.name })}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {selected && (
        <ResourceDetailDialog
          provider={active}
          item={selected.item}
          category={selected.category}
          siblings={
            (activeProvider?.categories || []).find((c) => c.name === selected.category)
              ?.resources || []
          }
          onOpenSibling={(item) => setSelected({ item, category: selected.category })}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
